/** COPYRIGHT NOTICE
 *
 *  Copyright (C) Vokke.
 *  All Rights Reserved
 *
 *  This file was developed by Vokke and is only to be used by a person or legal
 *  entity when expressly granted a license to do so through written means.
 *
 *  Unauthorized usage, sharing or copying of this file, via any medium is
 *  strictly prohibited. Proprietary and confidential. This license header
 *  is not to be removed.
 *
 */

import { Application, View } from "marionette";
import "jquery";
import ContentRegion from "./content-region";
import raf from "raf";
import Radio from "backbone.radio";

/**
 *  A layout manager class used simply to define the regions in the application.
 */
class LayoutManagerClass extends View {

    /**
     * Setup our class before we initialize
     */
    constructor () {
        super();
        this.el = "#body-region";
        this.template = false;

        this.regions = {
            contentRegion: {
                contentRegion: ContentRegion,
                el: "#content-region"
            },
            modalRegion: "#modal-region",
            navigationRegion: "#navigation-region"
        };

        this.addRegions(this.regions);
    }

}

/**
 *  The initial-style application kernel object.
 */
class ApplicationClass extends Application {

    /**
     * Setup our class before we initialize
     */
    constructor (...args) {
        super(...args);

        this.region = "body";
        this.modalIndexCounter = 0;
        this.globalRadio = Radio.channel("app:global");
        this.modalRadio = Radio.channel("app:modals");
    }

    /**
     *  Called when the application starts. We boot the Backbone history
     *  object and go.
     */
    onStart () {
        const that = this;
        this.layout = new LayoutManagerClass();
        Backbone.history.start();

        /** Treat navigation as a code-split module **/
        System.import("../../domain/components/Navigation").then(View => {
            this.__navigation = new View.default;
            raf(_ => this.navigationRegion.show(this.__navigation));
        });

        this.globalRadio.on("title:update", (options) => {
            raf(_ => {
                if(undefined === window.document) {
                    return;
                }

                let title = "Harmonee";

                if (undefined !== options.title) {
                    title += " - " + options.title;
                }

                window.document.title = title;
            });
        });

        window.document.addEventListener("click", (event) => {
            this.globalRadio.trigger("body:click", event);
        }, { passive: true });

        this.modalRadio.on("show:modal", options => {
            const modal = new options.modal(options.options);

            this.modalRegion.show(modal);

            modal.on("modalCompleted", data => {
                this.modalRadio.trigger("modal:saved:response", data);
            });
        });

    }

    showNavigation () {
        raf(_ => this.contentRegion.$el[0].removeAttribute("style"));
        raf(_ => this.navigationRegion.$el[0].removeAttribute("style"));
    }

    hideNavigation () {
        raf(_ => this.contentRegion.$el.css("margin-top", "0"));
        raf(_ => this.navigationRegion.$el.hide());
    }

    /**
     *  Returns a Marionette region object. This content region is where the main page
     *  content should be placed.
     *
     * @returns {*}
     */
    get contentRegion () {
        return this.layout.getRegion("contentRegion");
    }

    /**
     *  Returns a Marionette region object. This modal region is where all modals
     *  should be placed.
     *
     * @returns {*}
     */
    get modalRegion () {
        return this.layout.getRegion("modalRegion");
    }

    /**
     *  Returns a Marionette region object. This will be where the navigation is contained.
     *
     * @returns {*}
     */
    get navigationRegion () {
        return this.layout.getRegion("navigationRegion");
    }

    get navigation () {
        return this.__navigation;
    }

    /**
     *  Each modal is contained within its own region. Because modals are instantiated dynamically
     *  at run-time, this method 1) creates a new container DOM element for the region, 2)
     *  assigns it to a new region, 3) shows the modal in the region and 4) manages the new
     *  modal through Bootstrap's modal system.
     *
     * @param modal
     */
    showModal (modal){
        /** We keep a global counter going so we can uniquely name each modal region. **/
        this.modalIndexCounter++;

        /** We create a name for the view and a container for the region itself. **/
        const viewName = "view-" + this.modalIndexCounter;
        let container = this.modalRegion.$el.append("<div id='" + viewName + "'>");

        /** Add the region and show the modal inside it **/
        this.layout.addRegion(viewName, "#" + viewName);
        this.layout.getRegion(viewName).show(modal);

        /** Manage the modal through bootstrap **/
        let modalInstance = new Modal.default(container.find("[role='dialog']")[0]);
        modalInstance.show();
    }

}

export { ApplicationClass as Application };



// WEBPACK FOOTER //
// ./public/kernel/abstraction/application.js